import React from 'react';
import { Link } from 'react-router-dom';

// IdLink - the one style for an identifier that opens its record: a Task ID that opens the task,
// a Project # that opens the project (W-L10, W-M4.8).
//
// Before: Project # was a smaller, always-underlined link; Task IDs were dark pills on Project
// Work and All Tasks whether or not they did anything. Now an ID that opens something is a blue
// link at the surrounding text size (underlined on hover/focus); an ID that is only a label is
// plain text - render it as-is, or with `IdText` when it should keep tabular figures.

export const ID_LINK_CLASS =
  'wrap-id-link tw-inline tw-cursor-pointer tw-whitespace-nowrap tw-border-0 tw-bg-transparent tw-p-0 tw-font-medium tw-tabular-nums tw-text-primary tw-no-underline hover:tw-underline focus-visible:tw-underline focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-line-focus';

type Common = { children: React.ReactNode; className?: string; title?: string; 'aria-label'?: string };

export type IdLinkProps =
  | (Common & { to: string; href?: never; onClick?: (e: React.MouseEvent) => void })
  | (Common & { href: string; to?: never; target?: string; rel?: string; onClick?: (e: React.MouseEvent) => void })
  | (Common & { onClick: (e: React.MouseEvent) => void; to?: never; href?: never });

export const IdLink: React.FC<IdLinkProps> = (props) => {
  const { children, className = '', title, onClick } = props;
  const cls = `${ID_LINK_CLASS} ${className}`;
  const aria = props['aria-label'];
  if ('to' in props && props.to) {
    return (
      <Link to={props.to} className={cls} title={title} aria-label={aria} onClick={onClick}>
        {children}
      </Link>
    );
  }
  if ('href' in props && props.href) {
    return (
      <a href={props.href} target={props.target} rel={props.rel} className={cls} title={title} aria-label={aria} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <button
      type="button"
      className={cls}
      title={title}
      aria-label={aria}
      onClick={(e) => {
        // ID links usually sit in clickable rows; opening the record must not also fire the row.
        e.stopPropagation();
        onClick?.(e);
      }}
    >
      {children}
    </button>
  );
};

/** An identifier shown only as a label: plain text with tabular figures, never a pill. */
export const IdText: React.FC<React.HTMLAttributes<HTMLSpanElement>> = ({ className = '', ...rest }) => (
  <span className={`tw-whitespace-nowrap tw-tabular-nums ${className}`} {...rest} />
);

export default IdLink;
